import { NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useFgp } from "@/context/FgpContext";
import {
  LayoutDashboard, Users, GitBranch, Calendar, CheckSquare, Contact,
  FileText, RefreshCw, Bell, BarChart3, Settings, UserCog, Shield, LogOut, Mic,
  Sparkles, Plane, Receipt, Wrench, Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";

const groups = [
  { section: "Operations", items: [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: "/app/brief", label: "Daily Brief", icon: Sparkles, id: "brief" },
    { to: "/app/people", label: "People", icon: Users, id: "people" },
    { to: "/app/relationships", label: "Relationships", icon: GitBranch, id: "relationships" },
    { to: "/app/calendar", label: "Calendar", icon: Calendar, id: "calendar" },
    { to: "/app/tasks", label: "Tasks", icon: CheckSquare, id: "tasks" },
    { to: "/app/notes", label: "Notes", icon: Mic, id: "notes" },
    { to: "/app/tools", label: "Tools", icon: Wrench, id: "tools" },
    { to: "/app/budgeting", label: "Budgeting", icon: Wallet, id: "budgeting" },
  ]},
  { section: "Records", items: [
    { to: "/app/contacts", label: "Contacts", icon: Contact, id: "contacts" },
    { to: "/app/documents", label: "Documents", icon: FileText, id: "documents" },
    { to: "/app/renewals", label: "Renewals", icon: RefreshCw, id: "renewals" },
    { to: "/app/travel", label: "Travel", icon: Plane, id: "travel" },
    { to: "/app/expenses", label: "Expenses", icon: Receipt, id: "expenses" },
    { to: "/app/alerts", label: "Alerts", icon: Bell, id: "alerts" },
    { to: "/app/reports", label: "Reports", icon: BarChart3, id: "reports" },
  ]},
  { section: "Administration", items: [
    { to: "/app/settings", label: "Settings", icon: Settings, id: "settings" },
    { to: "/app/users", label: "User Management", icon: UserCog, id: "users" },
  ]},
];

export const SidebarContent = ({ onNavigate }) => {
  const { logout, workspace, currentUser } = useFgp();
  const navigate = useNavigate();
  const handleLogout = () => { onNavigate?.(); logout(); navigate("/login"); };
  const linkClass = ({ isActive }) => cn(
    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
    isActive ? "bg-neutral-50 text-neutral-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
  );

  return (
    <>
      <div className="h-28 flex items-center px-6 border-b border-slate-100"><Logo className="h-20" /></div>

      <div className="px-4 py-3 border-b border-slate-100">
        <p className="text-xs text-slate-400">Workspace</p>
        <p className="text-sm font-semibold text-slate-800 truncate">{workspace.name}</p>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {groups.map((group) => (
          <div key={group.section}>
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{group.section}</p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink key={item.id} to={item.to} data-testid={`nav-${item.id}`} onClick={() => onNavigate?.()} className={linkClass}>
                  <item.icon className="h-[18px] w-[18px]" />{item.label}
                </NavLink>
              ))}
              {group.section === "Administration" && currentUser?.isPlatformAdmin && (
                <NavLink to="/app/admin" data-testid="nav-admin" onClick={() => onNavigate?.()} className={linkClass}>
                  <Shield className="h-[18px] w-[18px]" /> Platform Admin
                  <span className="ml-auto text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Admin</span>
                </NavLink>
              )}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-slate-100">
        <button onClick={handleLogout} data-testid="sidebar-logout"
          className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-rose-50 hover:text-rose-600 transition-colors">
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </button>
      </div>
    </>
  );
};

export const Sidebar = () => {
  return (
    <aside className="w-64 flex-shrink-0 border-r border-slate-200 bg-white h-screen sticky top-0 hidden lg:flex flex-col" data-testid="app-sidebar">
      <SidebarContent />
    </aside>
  );
};
